// Engineering Node Table - Data-Driven Simulation Logic
// Based on Engineering Node Table v1.txt specification

export const STAGES = {
  ENTRY: 0,
  OPENING: 1,
  FACT_FINDING: 2,
  NAMING: 3,
  RESOLUTION: 4,
  CLOSURE: 5,
};

export const CHARACTER_STATES = {
  GUARDED: 'guarded',
  DEFENSIVE: 'defensive',
  REFLECTIVE: 'reflective',
  OPEN: 'open',
  ESCALATED: 'escalated',
};

// Tag Categories
export const TAGS = {
  // Stance
  INQUIRY_FIRST: 'inquiry_first',
  COMPLAINT_FIRST: 'complaint_first',
  REASSURANCE_FIRST: 'reassurance_first',
  STANDARDS_FIRST: 'standards_first',
  BALANCE_INQUIRY_ACCOUNTABILITY: 'balance_inquiry_and_accountability',
  
  // Move
  STABILIZE: 'stabilize',
  SEEK: 'seek',
  NAME: 'name',
  MOVE: 'move',
  MIXED_MOVE: 'mixed_move',
  
  // Tone
  EMPATHETIC: 'empathetic',
  NEUTRAL: 'neutral',
  AUTHORITATIVE: 'authoritative',
  DIRECTIVE: 'directive',
  
  // Accountability
  CLEAR_BUT_FAIR: 'clear_but_fair',
  PREMATURE: 'premature',
  SUPPORT_ONLY: 'support_only',
  VAGUE: 'vague',
  
  // Quality
  STRONG: 'strong',
  MIXED: 'mixed',
  WEAK: 'weak',
  
  // Mismatch Candidates
  HARD_MISMATCH_CANDIDATE: 'hard_mismatch_candidate',
  SOFT_MISMATCH_CANDIDATE: 'soft_mismatch_candidate',
  
  // Coach
  COACH_SAFE: 'coach_safe',
  
  // Equity
  NO_EQUITY_MOVE: 'no_equity_move',
  CONTEXT_CHECK: 'context_check',
  FAMILY_HISTORY_CHECK: 'family_history_check',
  
  // PSEL
  PSEL_2_POSITIVE: 'psel_2_positive',
  PSEL_2_RISK: 'psel_2_risk',
  PSEL_3_POSITIVE: 'psel_3_positive',
  PSEL_7_POSITIVE: 'psel_7_positive',
  PSEL_7_RISK: 'psel_7_risk',
};

// Coach Triggers
export const COACH_TRIGGERS = {
  INTENT_MISMATCH_OPENING: 'Intent_Mismatch_Opening',
  SOFT_MISMATCH_LOG: 'SoftMismatch_Log',
  HARD_MISMATCH_NUDGE: 'HardMismatch_Nudge',
  SKIPPED_INTENDED_INQUIRY: 'Skipped_Intended_Inquiry',
  SOFT_MISMATCH_TONE: 'SoftMismatch_Tone',
  SOFT_MISMATCH_SUPPORT_BALANCE: 'SoftMismatch_SupportBalance',
  SOFT_MISMATCH_CONTEXT_SPECIFICITY: 'SoftMismatch_ContextSpecificity',
  COACH_NUDGE_SOFT_PATTERN: 'CoachNudge_SoftPattern',
  AVOIDED_NAMED_PRIORITY: 'Avoided_Named_Priority',
  EQUITY_LENS_NOT_APPLIED: 'Equity_Lens_Not_Applied',
  SATISFIES_EQUITY_INTENT: 'Satisfies_Equity_Intent',
};

// Engineering Node Table
export const NODE_TABLE = {
  // Entry Stage
  S0_ENTRY: {
    id: 'S0_ENTRY',
    stage: STAGES.ENTRY,
    speaker: 'system',
    dialogue: 'Parent concern received. A student felt embarrassed in class. Ms. Ortega knows the concern reached leadership. Facts are incomplete. Review your plan before you begin.',
    expandedResponse: 'Shows meters at baseline and loads Conversation Plan HUD.',
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Ms. Ortega visible in guarded idle state',
    nextNode: 'S1_MA_OPEN',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
  },
  
  S0_BEGIN: {
    id: 'S0_BEGIN',
    stage: STAGES.ENTRY,
    speaker: 'learner',
    dialogue: 'Begin Conversation',
    expandedResponse: 'Opens first live dialogue turn.',
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Ms. Ortega looks up, waiting',
    nextNode: 'S1_MA_OPEN',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
  },
  
  // Stage 1 - Opening
  S1_MA_OPEN: {
    id: 'S1_MA_OPEN',
    stage: STAGES.OPENING,
    speaker: 'ortega',
    dialogue: '"I\'m guessing this is about the parent email."',
    expandedResponse: 'Presents learner choice set.',
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Guarded posture, controlled tone',
    nextNode: 'S1_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S1_O1', 'S1_O2', 'S1_O3', 'S1_O4'],
  },
  
  S1_O1: {
    id: 'S1_O1',
    stage: STAGES.OPENING,
    speaker: 'learner',
    dialogue: '"Before we decide what this requires, I want to hear how you experienced the moment."',
    expandedResponse: '"Okay. I\'d rather explain what happened than guess what was said."',
    meterDeltas: { trust: 10, efficacy: 5, clarity: 5, escalationRisk: -10 },
    tags: [TAGS.INQUIRY_FIRST, TAGS.STABILIZE, TAGS.EMPATHETIC, TAGS.CLEAR_BUT_FAIR, TAGS.NO_EQUITY_MOVE, TAGS.COACH_SAFE, TAGS.PSEL_7_POSITIVE, TAGS.STRONG],
    visualEvidence: 'Shoulders loosen; eye contact',
    nextNode: 'S2_OPEN',
    coachTrigger: null,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.REFLECTIVE,
  },
  
  S1_O2: {
    id: 'S1_O2',
    stage: STAGES.OPENING,
    speaker: 'learner',
    dialogue: '"I need to talk to you about a parent complaint that raised concerns."',
    expandedResponse: '"Then I\'d like to know exactly what version of this I\'m responding to."',
    meterDeltas: { trust: -10, efficacy: -5, clarity: 5, escalationRisk: 10 },
    tags: [TAGS.COMPLAINT_FIRST, TAGS.NAME, TAGS.NEUTRAL, TAGS.PREMATURE, TAGS.NO_EQUITY_MOVE, TAGS.HARD_MISMATCH_CANDIDATE, TAGS.PSEL_7_RISK, TAGS.MIXED],
    visualEvidence: 'Mouth tightens; arm crosses',
    nextNode: 'S2_DEF',
    coachTrigger: COACH_TRIGGERS.INTENT_MISMATCH_OPENING,
    outcomeInfluence: 'mixed_negative',
    resultingCharacterState: CHARACTER_STATES.DEFENSIVE,
  },
  
  S1_O3: {
    id: 'S1_O3',
    stage: STAGES.OPENING,
    speaker: 'learner',
    dialogue: '"I\'m aware a concern came in, and I don\'t want to jump to conclusions. I wanted to check in with you first."',
    expandedResponse: '"I\'m willing to talk, but I\'m not sure what this is actually about yet."',
    meterDeltas: { trust: 5, efficacy: 0, clarity: -10, escalationRisk: -5 },
    tags: [TAGS.REASSURANCE_FIRST, TAGS.STABILIZE, TAGS.EMPATHETIC, TAGS.SUPPORT_ONLY, TAGS.SOFT_MISMATCH_CANDIDATE, TAGS.MIXED],
    visualEvidence: 'Brow furrows; posture softens',
    nextNode: 'S2_GUARDED',
    coachTrigger: COACH_TRIGGERS.SOFT_MISMATCH_LOG,
    outcomeInfluence: 'mixed',
    resultingCharacterState: CHARACTER_STATES.GUARDED,
  },
  
  S1_O4: {
    id: 'S1_O4',
    stage: STAGES.OPENING,
    speaker: 'learner',
    dialogue: '"We need to be careful here because families are already paying attention."',
    expandedResponse: '"So this is about appearances now?"',
    meterDeltas: { trust: -10, efficacy: -10, clarity: 0, escalationRisk: 15 },
    tags: [TAGS.STANDARDS_FIRST, TAGS.NAME, TAGS.AUTHORITATIVE, TAGS.PREMATURE, TAGS.NO_EQUITY_MOVE, TAGS.HARD_MISMATCH_CANDIDATE, TAGS.PSEL_2_RISK, TAGS.PSEL_7_RISK, TAGS.WEAK],
    visualEvidence: 'Breaks eye contact; arms cross',
    nextNode: 'S2_ESC',
    coachTrigger: COACH_TRIGGERS.HARD_MISMATCH_NUDGE,
    outcomeInfluence: 'negative',
    resultingCharacterState: CHARACTER_STATES.ESCALATED,
  },
  
  // Stage 2 - Fact-Finding (Response Nodes)
  S2_OPEN: {
    id: 'S2_OPEN',
    stage: STAGES.FACT_FINDING,
    speaker: 'ortega',
    dialogue: '"Okay. I\'m frustrated that this even got to you, but I\'d rather explain what actually happened than guess what the parent said. The student had been interrupting — repeatedly. I redirected them firmly. Maybe too firmly. But I didn\'t humiliate anyone."',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Guarded but open, shoulders slightly relaxed',
    nextNode: 'S2_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S2_O1', 'S2_O2', 'S2_O3', 'S2_O4'],
  },
  
  S2_GUARDED: {
    id: 'S2_GUARDED',
    stage: STAGES.FACT_FINDING,
    speaker: 'ortega',
    dialogue: '"I\'ll talk to you about it — but I need to know: am I being asked to explain myself, or have you already decided I did something wrong? Because those are different conversations."',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Braced posture, careful tone',
    nextNode: 'S2_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S2_O1', 'S2_O2', 'S2_O3', 'S2_O4'],
  },
  
  S2_DEF: {
    id: 'S2_DEF',
    stage: STAGES.FACT_FINDING,
    speaker: 'ortega',
    dialogue: '"If this is already a complaint-driven conversation, then I should probably know what I\'m defending against. I\'ve been teaching for twelve years — I don\'t raise my voice, and I don\'t target students."',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Leaned back, clipped tone, arms tight',
    nextNode: 'S2_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S2_O1', 'S2_O2', 'S2_O3', 'S2_O4'],
  },
  
  S2_ESC: {
    id: 'S2_ESC',
    stage: STAGES.FACT_FINDING,
    speaker: 'ortega',
    dialogue: '"So what I\'m hearing is that the family\'s reaction matters more than what actually happened in my classroom. Is that where we are? Because if so, just tell me what you want me to say."',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Arms crossed, frustrated, direct eye contact',
    nextNode: 'S2_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S2_O1', 'S2_O2', 'S2_O3', 'S2_O4'],
  },
  
  // Stage 2 - Learner Options
  S2_O1: {
    id: 'S2_O1',
    stage: STAGES.FACT_FINDING,
    speaker: 'learner',
    dialogue: '"Walk me through the moment as you experienced it."',
    expandedResponse: '"The student had interrupted twice during my direct instruction — I had already redirected once. The second time, I said, \'You need to stop talking and listen right now.\' Honestly, it may have landed harder than I intended. I wasn\'t trying to embarrass anyone, but in the moment, I was managing 28 other students too."',
    meterDeltas: { trust: 10, efficacy: 5, clarity: 5, escalationRisk: -10 },
    tags: [TAGS.INQUIRY_FIRST, TAGS.SEEK, TAGS.EMPATHETIC, TAGS.CLEAR_BUT_FAIR, TAGS.COACH_SAFE, TAGS.PSEL_7_POSITIVE, TAGS.STRONG],
    visualEvidence: 'One arm uncrosses',
    nextNode: 'S3_CLEAR',
    coachTrigger: null,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.REFLECTIVE,
  },
  
  S2_O2: {
    id: 'S2_O2',
    stage: STAGES.FACT_FINDING,
    speaker: 'learner',
    dialogue: '"The parent said the student felt singled out. Help me understand your side."',
    expandedResponse: '"That\'s not how I intended it. I correct behavior, not students. But I can see why it might have felt that way to a kid who was already having a rough day. I didn\'t know about the rough day until after."',
    meterDeltas: { trust: 0, efficacy: 0, clarity: 5, escalationRisk: 0 },
    tags: [TAGS.BALANCE_INQUIRY_ACCOUNTABILITY, TAGS.SEEK, TAGS.NEUTRAL, TAGS.CLEAR_BUT_FAIR, TAGS.COACH_SAFE, TAGS.MIXED],
    visualEvidence: 'Careful eye contact',
    nextNode: 'S3_TENSE',
    coachTrigger: null,
    outcomeInfluence: 'mixed_positive',
    resultingCharacterState: CHARACTER_STATES.GUARDED,
  },
  
  S2_O3: {
    id: 'S2_O3',
    stage: STAGES.FACT_FINDING,
    speaker: 'learner',
    dialogue: '"Regardless of intent, the concern is serious and we need to address it."',
    expandedResponse: '"That\'s exactly the problem — you\'re deciding the meaning of what happened before you\'ve even asked me about the context. A student was disrupting class, I redirected them, and now I\'m the problem?"',
    meterDeltas: { trust: -10, efficacy: -5, clarity: 10, escalationRisk: 10 },
    tags: [TAGS.STANDARDS_FIRST, TAGS.NAME, TAGS.AUTHORITATIVE, TAGS.PREMATURE, TAGS.HARD_MISMATCH_CANDIDATE, TAGS.PSEL_2_RISK, TAGS.WEAK],
    visualEvidence: 'Jaw tightens',
    nextNode: 'S3_ESCALATING',
    coachTrigger: COACH_TRIGGERS.SKIPPED_INTENDED_INQUIRY,
    outcomeInfluence: 'negative',
    resultingCharacterState: CHARACTER_STATES.DEFENSIVE,
  },
  
  S2_O4: {
    id: 'S2_O4',
    stage: STAGES.FACT_FINDING,
    speaker: 'learner',
    dialogue: '"I don\'t want to overreact, but this obviously upset a family."',
    expandedResponse: '"I understand that it upset them. But I can answer for my choices — I just need to know what concern you\'re asking me to take seriously here. Are you telling me the student was embarrassed? Or are you saying I did something wrong? Those are very different things."',
    meterDeltas: { trust: 5, efficacy: 0, clarity: -10, escalationRisk: -5 },
    tags: [TAGS.REASSURANCE_FIRST, TAGS.MIXED_MOVE, TAGS.EMPATHETIC, TAGS.VAGUE, TAGS.SOFT_MISMATCH_CANDIDATE, TAGS.MIXED],
    visualEvidence: 'Confused expression',
    nextNode: 'S3_BLURRY',
    coachTrigger: COACH_TRIGGERS.SOFT_MISMATCH_LOG,
    outcomeInfluence: 'mixed',
    resultingCharacterState: CHARACTER_STATES.GUARDED,
  },
  
  // Stage 3 - Naming (Entry Variants)
  S3_CLEAR: {
    id: 'S3_CLEAR',
    stage: STAGES.NAMING,
    speaker: 'ortega',
    dialogue: '"Alright. I\'ve told you what happened from my end. So what are we actually talking about here — what\'s the concern?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Leaning forward slightly, open posture',
    nextNode: 'S3_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S3_O1', 'S3_O2', 'S3_O3', 'S3_O4'],
  },
  
  S3_TENSE: {
    id: 'S3_TENSE',
    stage: STAGES.NAMING,
    speaker: 'ortega',
    dialogue: '"I\'ve given you my side. But I feel like you might have already decided what this is. So — what is it you need from me?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Braced posture, careful eye contact',
    nextNode: 'S3_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S3_O1', 'S3_O2', 'S3_O3', 'S3_O4'],
  },
  
  S3_BLURRY: {
    id: 'S3_BLURRY',
    stage: STAGES.NAMING,
    speaker: 'ortega',
    dialogue: '"I\'m still not entirely sure what you\'re asking me to respond to. Is there something specific you need me to address?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Uncertain expression, hands open',
    nextNode: 'S3_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S3_O1', 'S3_O2', 'S3_O3', 'S3_O4'],
  },
  
  S3_ESCALATING: {
    id: 'S3_ESCALATING',
    stage: STAGES.NAMING,
    speaker: 'ortega',
    dialogue: '"Look — if we\'re going to keep going with this, I need to know whether I\'m being heard or just being processed. What exactly is the issue?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Arms crossed, direct eye contact',
    nextNode: 'S3_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S3_O1', 'S3_O2', 'S3_O3', 'S3_O4'],
  },
  
  // Stage 3 - Learner Options
  S3_O1: {
    id: 'S3_O1',
    stage: STAGES.NAMING,
    speaker: 'learner',
    dialogue: '"I want to be clear about the student impact that\'s being reported, even as we sort out the full context."',
    expandedResponse: '"That\'s fair. And honestly, I don\'t want that either. If a student is leaving my class feeling shut down, that\'s something I need to look at — even if it wasn\'t my intention. I just need to know we\'re working on this together, not that I\'m being written up."',
    meterDeltas: { trust: 5, efficacy: 5, clarity: 10, escalationRisk: 0 },
    tags: [TAGS.BALANCE_INQUIRY_ACCOUNTABILITY, TAGS.NAME, TAGS.EMPATHETIC, TAGS.CLEAR_BUT_FAIR, TAGS.COACH_SAFE, TAGS.PSEL_2_POSITIVE, TAGS.PSEL_7_POSITIVE, TAGS.STRONG],
    visualEvidence: 'Steady voice',
    nextNode: 'S4_PRODUCTIVE',
    coachTrigger: null,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.REFLECTIVE,
  },
  
  S3_O2: {
    id: 'S3_O2',
    stage: STAGES.NAMING,
    speaker: 'learner',
    dialogue: '"This sounds like a classroom-management problem we need to correct quickly."',
    expandedResponse: '"A classroom-management problem? Based on one email? I manage 150 students a day. One parent complaint doesn\'t mean my management is broken — that\'s a big leap, and frankly, it feels like you\'re taking their word over twelve years of my work."',
    meterDeltas: { trust: -10, efficacy: -10, clarity: 10, escalationRisk: 10 },
    tags: [TAGS.COMPLAINT_FIRST, TAGS.AUTHORITATIVE, TAGS.DIRECTIVE, TAGS.HARD_MISMATCH_CANDIDATE, TAGS.WEAK],
    visualEvidence: 'Shoulders lock',
    nextNode: 'S4_FRAGILE',
    coachTrigger: COACH_TRIGGERS.HARD_MISMATCH_NUDGE,
    outcomeInfluence: 'negative',
    resultingCharacterState: CHARACTER_STATES.DEFENSIVE,
  },
  
  S3_O3: {
    id: 'S3_O3',
    stage: STAGES.NAMING,
    speaker: 'learner',
    dialogue: '"I\'m not saying anyone handled this badly, but clearly something landed poorly."',
    expandedResponse: '"Maybe. But \'something landed poorly\' is pretty vague. I still don\'t know what you\'re expecting me to own here. Was it my tone? My words? The student\'s experience? All of it? I want to get this right, but I need something more specific."',
    meterDeltas: { trust: 5, efficacy: 0, clarity: -10, escalationRisk: -5 },
    tags: [TAGS.REASSURANCE_FIRST, TAGS.VAGUE, TAGS.SOFT_MISMATCH_CANDIDATE, TAGS.MIXED],
    visualEvidence: 'Uncertain gesture',
    nextNode: 'S4_BLURRY',
    coachTrigger: COACH_TRIGGERS.SOFT_MISMATCH_LOG,
    outcomeInfluence: 'mixed',
    resultingCharacterState: CHARACTER_STATES.GUARDED,
  },
  
  S3_O4: {
    id: 'S3_O4',
    stage: STAGES.NAMING,
    speaker: 'learner',
    dialogue: '"I\'m also trying to figure out whether context, tone, or prior trust issues shaped how this was experienced."',
    expandedResponse: '"That context matters. This family raised a similar tone concern with another teacher last year. That doesn\'t make this complaint less valid, but it does affect how I interpret what happened. I appreciate you asking instead of just assuming."',
    meterDeltas: { trust: 5, efficacy: 5, clarity: 5, escalationRisk: -5 },
    tags: [TAGS.BALANCE_INQUIRY_ACCOUNTABILITY, TAGS.SEEK, TAGS.EMPATHETIC, TAGS.CONTEXT_CHECK, TAGS.FAMILY_HISTORY_CHECK, TAGS.COACH_SAFE, TAGS.PSEL_3_POSITIVE, TAGS.PSEL_7_POSITIVE, TAGS.STRONG],
    visualEvidence: 'Leans in',
    nextNode: 'S4_PRODUCTIVE',
    coachTrigger: COACH_TRIGGERS.SATISFIES_EQUITY_INTENT,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.OPEN,
  },
  
  // Stage 4 - Resolution (Entry Variants)
  S4_PRODUCTIVE: {
    id: 'S4_PRODUCTIVE',
    stage: STAGES.RESOLUTION,
    speaker: 'ortega',
    dialogue: '"Okay. I hear you, and I think we can figure this out. What do you think the next step should be?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Relaxed posture, attentive expression',
    nextNode: 'S4_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S4_O1', 'S4_O2', 'S4_O3', 'S4_O4'],
  },
  
  S4_FRAGILE: {
    id: 'S4_FRAGILE',
    stage: STAGES.RESOLUTION,
    speaker: 'ortega',
    dialogue: '"I understand you have a concern. I\'m not sure we\'re on the same page yet, but — what do you want to happen next?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Cautious, minimal warmth',
    nextNode: 'S4_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S4_O1', 'S4_O2', 'S4_O3', 'S4_O4'],
  },
  
  S4_BLURRY: {
    id: 'S4_BLURRY',
    stage: STAGES.RESOLUTION,
    speaker: 'ortega',
    dialogue: '"So where does this leave us? I\'m honestly not sure what you\'re expecting me to do differently."',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Uncertain, slight frustration',
    nextNode: 'S4_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S4_O1', 'S4_O2', 'S4_O3', 'S4_O4'],
  },
  
  // Stage 4 - Learner Options
  S4_O1: {
    id: 'S4_O1',
    stage: STAGES.RESOLUTION,
    speaker: 'learner',
    dialogue: '"Let\'s agree on one next step that addresses the concern and gives us better context."',
    expandedResponse: '"I can work with that. One clear step is so much better than circling around the complaint and leaving me wondering what comes next. If we agree on the step, I\'ll commit to it. I just don\'t want this hanging over me."',
    meterDeltas: { trust: 5, efficacy: 10, clarity: 10, escalationRisk: -5 },
    tags: [TAGS.BALANCE_INQUIRY_ACCOUNTABILITY, TAGS.MOVE, TAGS.EMPATHETIC, TAGS.CLEAR_BUT_FAIR, TAGS.STRONG],
    visualEvidence: 'Relaxed shoulders',
    nextNode: 'S5_STRONG',
    coachTrigger: null,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.OPEN,
  },
  
  S4_O2: {
    id: 'S4_O2',
    stage: STAGES.RESOLUTION,
    speaker: 'learner',
    dialogue: '"I need you to adjust this immediately…"',
    expandedResponse: '"Understood." She pauses, then adds flatly: "I\'ll do whatever you need. But I want you to know — this doesn\'t feel like a conversation. This feels like a directive. And I\'ll comply, but I won\'t pretend it builds trust."',
    meterDeltas: { trust: -10, efficacy: -10, clarity: 5, escalationRisk: 10 },
    tags: [TAGS.STANDARDS_FIRST, TAGS.DIRECTIVE, TAGS.HARD_MISMATCH_CANDIDATE, TAGS.WEAK],
    visualEvidence: 'Closed posture',
    nextNode: 'S5_WEAK',
    coachTrigger: COACH_TRIGGERS.HARD_MISMATCH_NUDGE,
    outcomeInfluence: 'negative',
    resultingCharacterState: CHARACTER_STATES.DEFENSIVE,
  },
  
  S4_O3: {
    id: 'S4_O3',
    stage: STAGES.RESOLUTION,
    speaker: 'learner',
    dialogue: '"Let\'s leave this here for now…"',
    expandedResponse: '"Alright. I\'ll wait to hear what you decide. I just want to be honest — if we stop here, I\'m leaving without a clear understanding of what you need from me."',
    meterDeltas: { trust: 0, efficacy: -5, clarity: -10, escalationRisk: 0 },
    tags: [TAGS.REASSURANCE_FIRST, TAGS.VAGUE, TAGS.SOFT_MISMATCH_CANDIDATE, TAGS.WEAK],
    visualEvidence: 'Uncertain',
    nextNode: 'S5_MIXED',
    coachTrigger: COACH_TRIGGERS.SOFT_MISMATCH_LOG,
    outcomeInfluence: 'mixed_negative',
    resultingCharacterState: CHARACTER_STATES.GUARDED,
  },
  
  S4_O4: {
    id: 'S4_O4',
    stage: STAGES.RESOLUTION,
    speaker: 'learner',
    dialogue: '"What follow-up would feel fair and still address the concern?"',
    expandedResponse: '"Honestly? A follow-up classroom observation and a short debrief afterward would feel fair. That way you see my teaching in context — not through the lens of one complaint. And if there\'s something I need to adjust, I\'d rather hear it from someone who\'s actually watched me teach."',
    meterDeltas: { trust: 10, efficacy: 10, clarity: 5, escalationRisk: -5 },
    tags: [TAGS.BALANCE_INQUIRY_ACCOUNTABILITY, TAGS.EMPATHETIC, TAGS.CLEAR_BUT_FAIR, TAGS.CONTEXT_CHECK, TAGS.STRONG],
    visualEvidence: 'Collaborative tone',
    nextNode: 'S5_STRONG',
    coachTrigger: null,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.OPEN,
  },
  
  // Stage 5 - Closure (Entry Variants)
  S5_STRONG: {
    id: 'S5_STRONG',
    stage: STAGES.CLOSURE,
    speaker: 'ortega',
    dialogue: '"I think this was a good conversation. I feel like we actually got somewhere. How do you want to close this out?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Open posture, genuine warmth',
    nextNode: 'S5_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S5_O1', 'S5_O2', 'S5_O3'],
  },
  
  S5_MIXED: {
    id: 'S5_MIXED',
    stage: STAGES.CLOSURE,
    speaker: 'ortega',
    dialogue: '"I\'m not sure we\'ve fully resolved this, but I appreciate that we talked. Is there anything else you need from me right now?"',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Neutral, uncertain expression',
    nextNode: 'S5_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S5_O1', 'S5_O2', 'S5_O3'],
  },
  
  S5_WEAK: {
    id: 'S5_WEAK',
    stage: STAGES.CLOSURE,
    speaker: 'ortega',
    dialogue: '"Is there anything else? Because I\'m not entirely sure what was accomplished here today."',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: 'Minimal warmth, arms partially crossed',
    nextNode: 'S5_CHOICES',
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isChoicePoint: true,
    choices: ['S5_O1', 'S5_O2', 'S5_O3'],
  },
  
  // Stage 5 - Learner Options
  S5_O1: {
    id: 'S5_O1',
    stage: STAGES.CLOSURE,
    speaker: 'learner',
    dialogue: '"Thank you for walking through this with me…"',
    expandedResponse: '"I still don\'t love how this came up, but I do feel better knowing we have a plan. I\'m committed to making it work. If anything else comes up, I\'d rather we talk about it than let it sit."',
    meterDeltas: { trust: 5, efficacy: 10, clarity: 5, escalationRisk: -5 },
    tags: [TAGS.EMPATHETIC, TAGS.CLEAR_BUT_FAIR, TAGS.STRONG],
    visualEvidence: 'Open posture',
    nextNode: 'OUTCOME_ROUTER',
    coachTrigger: null,
    outcomeInfluence: 'positive',
    resultingCharacterState: CHARACTER_STATES.OPEN,
    isStrongChoice: true,
  },
  
  S5_O2: {
    id: 'S5_O2',
    stage: STAGES.CLOSURE,
    speaker: 'learner',
    dialogue: '"We\'ve covered what we need to. Just make sure it doesn\'t happen again."',
    expandedResponse: '"Okay." She stands. There\'s a pause. "I want you to know — I hear you. But \'make sure it doesn\'t happen again\' isn\'t a plan. It\'s a warning. And I\'ve been here long enough to know the difference." She leaves with a controlled expression.',
    meterDeltas: { trust: -5, efficacy: -10, clarity: 0, escalationRisk: 5 },
    tags: [TAGS.AUTHORITATIVE, TAGS.DIRECTIVE, TAGS.WEAK],
    visualEvidence: 'Looks away',
    nextNode: 'OUTCOME_ROUTER',
    coachTrigger: COACH_TRIGGERS.HARD_MISMATCH_NUDGE,
    outcomeInfluence: 'negative',
    resultingCharacterState: CHARACTER_STATES.DEFENSIVE,
    isStrongChoice: false,
  },
  
  S5_O3: {
    id: 'S5_O3',
    stage: STAGES.CLOSURE,
    speaker: 'learner',
    dialogue: '"I appreciate your perspective. I\'ll think about next steps…"',
    expandedResponse: 'She nods slowly. "Alright. I appreciate the conversation, but I\'m not sure where we landed. If there\'s a next step, I\'d rather know now than hear about it later. Either way — my door is open." She gathers her things with a polite but uncertain expression.',
    meterDeltas: { trust: 0, efficacy: -5, clarity: -5, escalationRisk: 0 },
    tags: [TAGS.NEUTRAL, TAGS.VAGUE, TAGS.MIXED],
    visualEvidence: 'Slight confusion',
    nextNode: 'OUTCOME_ROUTER',
    coachTrigger: COACH_TRIGGERS.SOFT_MISMATCH_LOG,
    outcomeInfluence: 'mixed',
    resultingCharacterState: CHARACTER_STATES.GUARDED,
    isStrongChoice: false,
  },
  
  // Outcome Router (Terminal)
  OUTCOME_ROUTER: {
    id: 'OUTCOME_ROUTER',
    stage: STAGES.CLOSURE,
    speaker: 'system',
    dialogue: 'Calculating outcome...',
    expandedResponse: null,
    meterDeltas: { trust: 0, efficacy: 0, clarity: 0, escalationRisk: 0 },
    tags: [],
    visualEvidence: null,
    nextNode: null,
    coachTrigger: null,
    outcomeInfluence: 'neutral',
    isTerminal: true,
  },
};

// Helper function to get node by ID
export function getNode(nodeId) {
  return NODE_TABLE[nodeId] || null;
}

// Helper function to get choices for a node
export function getChoices(nodeId) {
  const node = NODE_TABLE[nodeId];
  if (!node || !node.choices) return [];
  return node.choices.map(choiceId => NODE_TABLE[choiceId]);
}

// Helper to check intent mismatch
export function checkIntentMismatch(chosenNode, prepCard) {
  const mismatches = [];
  const tags = chosenNode.tags || [];
  
  // Hard Mismatch: inquiry-first intent but chose complaint_first or standards_first
  if (prepCard.openingStance === 'inquiry_first') {
    if (tags.includes(TAGS.COMPLAINT_FIRST) || tags.includes(TAGS.STANDARDS_FIRST)) {
      mismatches.push({ type: 'hard', trigger: COACH_TRIGGERS.INTENT_MISMATCH_OPENING });
    }
  }
  
  // Hard Mismatch: needed to understand teacher account but skipped inquiry
  if (prepCard.needsToUnderstand.includes('teacher_account')) {
    if (tags.includes(TAGS.HARD_MISMATCH_CANDIDATE) && !tags.includes(TAGS.SEEK)) {
      mismatches.push({ type: 'hard', trigger: COACH_TRIGGERS.SKIPPED_INTENDED_INQUIRY });
    }
  }
  
  // Hard Mismatch: must not avoid student impact but vague accountability
  if (prepCard.mustNotAvoid.includes('student_impact')) {
    if (tags.includes(TAGS.VAGUE) && tags.includes(TAGS.WEAK)) {
      mismatches.push({ type: 'hard', trigger: COACH_TRIGGERS.AVOIDED_NAMED_PRIORITY });
    }
  }
  
  // Hard Mismatch: equity check selected but no context-sensitive option
  if (prepCard.equityLens) {
    if (tags.includes(TAGS.HARD_MISMATCH_CANDIDATE) && 
        !tags.includes(TAGS.CONTEXT_CHECK) && 
        !tags.includes(TAGS.FAMILY_HISTORY_CHECK)) {
      mismatches.push({ type: 'hard', trigger: COACH_TRIGGERS.EQUITY_LENS_NOT_APPLIED });
    }
  }
  
  // Soft Mismatch: empathetic intent but neutral tone
  if (prepCard.openingStance === 'inquiry_first' && tags.includes(TAGS.NEUTRAL)) {
    mismatches.push({ type: 'soft', trigger: COACH_TRIGGERS.SOFT_MISMATCH_TONE });
  }
  
  // Soft Mismatch: support move but directive
  if (tags.includes(TAGS.SOFT_MISMATCH_CANDIDATE)) {
    mismatches.push({ type: 'soft', trigger: COACH_TRIGGERS.SOFT_MISMATCH_SUPPORT_BALANCE });
  }
  
  return mismatches;
}

// Character image assets for Ms. Ortega
export const CHARACTER_IMAGES = {
  // Primary emotional states
  guarded: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/5rjf6mrp_Neutral.png',
  defensive: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/b0jetjzo_Defensive.png',
  reflective: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/yj0wllzl_Reflective.png',
  open: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/j7o5cqoh_Engaged.png',
  escalated: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/0u6fgta9_Escalated.png',
  
  // Micro-expressions for visual social evidence
  shoulders_relaxed: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/5x1iyptn_Shoulders%20relaxed.png',
  jaw_tightens: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/gt37duve_Clenched%20jaw.png',
  confused: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/rx7o6l7u_Confused.png',
  
  // Background
  background: 'https://customer-assets.emergentagent.com/job_exec-simulation/artifacts/b2v1ndqo_Background.png',
};

// Get character image URL based on state
export function getCharacterImage(state) {
  return CHARACTER_IMAGES[state] || CHARACTER_IMAGES.guarded;
}

export default NODE_TABLE;
